/**
 * bz-quiz.js — Brainzyme® Quiz Engine v2.0 (Shopify Liquid port)
 *
 * v2.0 (2026-09-06, session 32, T-1cd020 / T-d6f007): TWO-POLE routing.
 * Recommendation is decided by two authored poles — depleted/under-stimulated (PRO) and
 * pressured/over-stimulated (ELITE) — never by a severity total. Calum's rulings: both poles
 * clearing a real threshold -> Starter Bundle; low signal -> Starter Bundle (never ORIGINAL);
 * suitability (age 10-15 / sensitivity) -> ORIGINAL; a life context never routes.
 * The 5 cluster scores are retained for the results bars, cause_flags and the driver pill only.
 * A config without scoring.method === 'two_pole' still takes the ORIGINAL v1 path unchanged, so a
 * cached v2 engine and a not-yet-updated data file behave exactly as they did before.
 * Brainzyme® Brain Fog Quiz · Variants A, B, C
 *
 * Shopify port from quiz-prototypes/_engine/quiz.js.
 * Key change: reads config from a Liquid-injected JSON blob
 * (<script type="application/json" id="bz-quiz-config">) rather than fetching
 * an external JSON file. This removes the async network dependency and makes
 * the engine work reliably behind Shopify CDN caching.
 *
 * Architecture:
 *   state  → single source of truth (cluster scores, current q, phase, etc.)
 *   render → pure functions: derive DOM from state, never read DOM for logic
 *   events → update state, call render
 *
 * Compliance guardrails (non-negotiable per spec §6):
 *   - no "nootropic" / "treat" / "cure" / "fix" in any generated copy
 *   - "medical professionals" only — never "doctors"
 *   - no "ADHD" as a condition Brainzyme® treats
 */

'use strict';

(function (global) {

  /* ─────────────────────────────────────────────
     CONSTANTS
  ───────────────────────────────────────────── */

  const CLUSTERS = ['sleep', 'stress', 'nutrition', 'hormones', 'overload'];

  const CLUSTER_LABELS = {
    sleep:     'Sleep & Recovery',
    stress:    'Stress & Mood',
    nutrition: 'Nutrition & Energy',
    hormones:  'Hormones & Life Stage',
    overload:  'Overload & Attention',
  };

  // 2026-05-24: per-market UI string overrides (T-5389b7).
  // Pattern: getCfg(state, 'key', 'English fallback'). Engine stays identical
  // across all 4 markets; per-market data files supply localized values.
  function getCfg(state, key, fallback) {
    return (state && state.config && state.config[key]) || fallback;
  }
  function getClusterLabel(state, c) {
    return (state && state.config && state.config.cluster_labels && state.config.cluster_labels[c]) || CLUSTER_LABELS[c];
  }

  const SKU_META = {
    ELITE: {
      name:             'FOCUS ELITE™',
      url_slug:         '/products/brainzyme-elite',
      price:            '£47',
      ingredient_count: 29,
      class2:           'stress-free focus and positive mood',
      class3:           'GABA',
      image_url:        'https://cdn.shopify.com/s/files/1/1719/2651/files/Packshot_Focus_Elite_2Packs_Website_ENG.jpg?v=1759318794',
    },
    PRO: {
      name:             'FOCUS PRO™',
      url_slug:         '/products/brainzyme-professional-stronger-formula',
      price:            '£29',
      ingredient_count: 20,
      class2:           'motivated mood and strong focus',
      class3:           'dopamine',
      image_url:        'https://cdn.shopify.com/s/files/1/1719/2651/files/Packshot_Focus_Pro_2Packs_Website_ENG_e13daa13-0e63-494d-9962-b8d96254ae21.jpg?v=1759318840',
    },
    ORIGINAL: {
      name:             'FOCUS ORIGINAL™',
      url_slug:         '/products/brainzyme-original-milder-formula',
      price:            '£18',
      ingredient_count: 12,
      class2:           'mild everyday focus and calm mood',
      class3:           'natural botanical blend',
      image_url:        'https://cdn.shopify.com/s/files/1/1719/2651/files/Packshot_Focus_Original_2Packs_Website_ENG.jpg?v=1759318901',
    },
    // 2026-07-17 (Calum): Starter Bundle restored as a first-class outcome.
    // UK-store defaults only — every live data file supplies per-market
    // name/url/price/image_url overrides via result_templates.BUNDLE.
    BUNDLE: {
      name:             'FOCUS™ Starter Bundle',
      url_slug:         '/products/brainzyme-combo-set-1x-elite-1x-professional-1x-original',
      price:            '£44',
      ingredient_count: 30,
      class2:           'all three formulas in one set',
      class3:           'dopamine + GABA',
      image_url:        'https://cdn.shopify.com/s/files/1/1719/2651/files/New_Hero_PP_BUNDLE_ENG.webp?v=1784117631',
    },
  };

  // Config-aware coercion (2026-07-17, was unconditional BUNDLE→ELITE while the
  // bundle was phased out). BUNDLE resolves as itself ONLY when the active data
  // file ships a result_templates.BUNDLE — variants without one (retired b/c/gd,
  // stale pre-2026-05-19 localStorage) keep the legacy ELITE coercion so the
  // result page always renders a valid template.
  function coerceLegacyBundle(sku, state) {
    if (sku !== 'BUNDLE') return sku;
    var hasBundleTpl = state && state.config && state.config.result_templates
      && state.config.result_templates.BUNDLE;
    return hasBundleTpl ? 'BUNDLE' : 'ELITE';
  }

  const SHOPIFY_BASE = 'https://www.brainzyme.com';

  /* ─────────────────────────────────────────────
     STATE (initialised per quiz instance)
  ───────────────────────────────────────────── */

  function freshState(config) {
    return {
      config,
      answers: {},
      scores: { sleep: 0, stress: 0, nutrition: 0, hormones: 0, overload: 0 },
      totalScore: 0,
      // v2 two-pole scores. Absent from every legacy config, in which case they stay 0/0 and the
      // legacy router (which never reads them) is used.
      poles: { pro: 0, elite: 0 },
      currentPhaseIndex: 0,
      currentQuestionIndexInPhase: 0,
      globalQuestionIndex: 0,
      totalQuestions: config.phases.reduce((n, p) => n + p.question_ids.length, 0),
      phaseComplete: false,
      outcome: null,
      dominantCluster: null,
      secondaryCluster: null,
      emailSubmitted: false,
      email: '',
      firstName: '',
      processingStarted: false,
      processingDone: false,
      outcomeRoute: null,
      presentedDriver: null,
      presentedDriverMode: null,
      utmParams: {},
      // Funnel-event tracking (added 2026-05-25, Step 8 of quiz-intake migration)
      // Per 3-reviewer QC pivot 2026-05-25: client emits bz_quiz_paused on
      // every visibility/unload signal; Worker server-infers "abandoned" from
      // session_state with no terminal event after 30 min. No bzAbandonedFired
      // flag — multi-fire of paused events is valid signal (user toggling
      // visibility yields multiple data points; the Worker dedupes).
      bzSessionId: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'bz-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10),
      bzStartedFired: false,
      bzCompletedFired: false,
      // 2026-09-15 (T-44e3b0, WS2 marker producer): internal QA walks flag themselves with ?bz_qa=1.
      // Read once per state so restore / retake / reset all carry it; the flag itself lives in
      // sessionStorage for the visit (see readQaFlag).
      bzQa: readQaFlag(),
    };
  }

  // The QA marker. `?bz_qa=1` on the page URL persists to sessionStorage for the rest of the visit,
  // so a later page (or a retake that drops the query) is still flagged. Every funnel event then
  // carries `bz_qa: 1` and the Worker excludes the session from the aggregate. Real customers never
  // carry it: the key is only ever written from the query string. Never throws (sessionStorage can
  // be unavailable in private mode / the Node fixtures).
  function readQaFlag() {
    try {
      if (typeof window === 'undefined') return false;
      var search = (window.location && window.location.search) || '';
      if (new URLSearchParams(search).get('bz_qa') === '1') {
        try { sessionStorage.setItem('bz_qa', '1'); } catch (_) {}
        return true;
      }
      return sessionStorage.getItem('bz_qa') === '1';
    } catch (_) {
      return false;
    }
  }

  /* ─────────────────────────────────────────────
     FUNNEL EVENT PUBLISHER (2026-05-25, Step 8)
     Fires bz_quiz_* events to Shopify Customer Events sandbox via
     window.analytics.publish. Subscribed by the Web Pixel Extension
     (extensions/bz-quiz-pixel) which forwards events to
     /apps/brainzyme-quiz/event (App Proxy) → CF Worker → KV → team
     funnel dashboard.

     Best-effort: if window.analytics is unavailable (pixel not installed,
     theme without Customer Events runtime, or sandbox restrictions), the
     call is a silent no-op. The quiz engine stays fully functional
     regardless of pixel state.
  ───────────────────────────────────────────── */
  function publishFunnelEvent(state, eventName, extra) {
    try {
      if (typeof window === 'undefined') return;
      // Funnel telemetry: direct POST to the Cloudflare Worker /api/event, the
      // same direct-fetch pattern bz-quiz-relay.js uses for the /api/quiz-submit
      // submission. No Web Pixel. Events are anonymous (no PII). text/plain + no
      // custom header keeps this a CORS "simple request" (no preflight); the
      // Worker accepts text/plain and JSON.parse()s the body. keepalive lets the
      // terminal bz_quiz_paused event survive page unload (iOS Safari). Fire-always
      // with no consent gate per Calum 2026-06-02. QC: Gemini 3.1 Pro GO 2026-06-02.
      var cfg = (state && state.config) || {};
      var utm = (state && state.utmParams) || {};
      var payload = Object.assign({
        event_name:  eventName,
        market:      cfg.market || 'uk',
        variant:     cfg.variant || 'a',
        session_id:  state && state.bzSessionId,
        primary_url: typeof location !== 'undefined' ? location.pathname : '',
        landing_url: utm.landing_url || (typeof location !== 'undefined' ? location.href : null),
        referrer:    typeof document !== 'undefined' ? (document.referrer || null) : null,
        ts:          Date.now(),
        utm_source:   utm.utm_source || null,
        utm_medium:   utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_content:  utm.utm_content || null,
        utm_term:     utm.utm_term || null,
        bz_wave:      utm.bz_wave || null,
        bz_lp:        utm.bz_lp || null,
        bz_product:   utm.bz_product || null,
        bz_test:      utm.bz_test || null,
        bz_creative:  utm.bz_creative || null
      }, extra || {});
      // T-44e3b0: only ever present on a flagged session — an absent key is a real customer.
      if (state && state.bzQa) payload.bz_qa = 1;
      fetch('https://white-flower-17a2-brainzyme-reviews.nutritionalproducts-org-account.workers.dev/api/event', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function () {});
    } catch (e) {
      // Funnel telemetry must never throw into the engine.
    }
  }

  /* ─────────────────────────────────────────────
     CART ATTRIBUTION (2026-09-15, session 32, T-40a3cd)
     54% of takers skip the email gate and leave no record, so their purchases
     are invisible. On a result buy-CTA click, stamp the visitor's cart with
     quiz attributes via the storefront Ajax API; Shopify copies cart
     attributes onto the order (Admin: order.customAttributes / REST
     note_attributes). Single-underscore keys are hidden from the customer at
     checkout but still returned by GET /cart.js, which is how a canary click
     is proven; a double-underscore key would be private and unverifiable.
     No PII: SKUs, route, lane, gate outcome, timestamp only.

     Best-effort and never on the click path: the fetch is not awaited, the
     link's default navigation is untouched, and any failure is swallowed.
     Known limit: a cart attribute lives as long as the cart, so a later
     purchase in the same cart is still attributed to the quiz (accepted).
  ───────────────────────────────────────────── */

  // Same Shopify store, ignoring a www. prefix. FR's primary domain is the bare brainzyme.fr while
  // its lane files carry storefront_base https://www.brainzyme.fr (a 301 hop, same cart) — strict
  // origin equality would silently disable attribution there. A genuinely different store (the
  // T-e68f8c misroute class) must NOT be stamped: the relative /cart/update.js would write to the
  // quiz page's cart, not the cart the customer buys from.
  function sameStoreHost(a, b) {
    var strip = function (h) { return String(h || '').toLowerCase().replace(/^www\./, ''); };
    return strip(a) !== '' && strip(a) === strip(b);
  }

  function postQuizCartAttributes(state, cta) {
    try {
      if (typeof window === 'undefined' || typeof fetch !== 'function' || !state || !cta) return false;
      var dest = new URL(cta.url, window.location.href);
      if (!sameStoreHost(dest.hostname, window.location.hostname)) return false;
      var cfg = state.config || {};
      var attributes = {
        _bz_quiz_variant: String(cfg.market || '') + '-' + String(cfg.variant || '').toLowerCase(),
        _bz_quiz_rec:     String(state.outcome || '').toLowerCase(),
        _bz_quiz_cta_sku: String(cta.sku || '').toLowerCase(),
        _bz_quiz_route:   state.outcomeRoute || '',
        _bz_quiz_gate:    state.emailSubmitted ? 'submitted' : 'skipped',
        _bz_quiz_page:    window.location.pathname || '',
        _bz_quiz_at:      new Date().toISOString(),
      };
      if (state.bzQa) attributes._bz_quiz_qa = '1';
      // Shopify.routes.root carries a locale prefix on multi-locale stores; '/' is the documented default.
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        keepalive: true
      }).catch(function () {});
      return true;
    } catch (e) {
      // Attribution must never break the buy click.
      return false;
    }
  }

  /* ─────────────────────────────────────────────
     SCORING ENGINE
  ───────────────────────────────────────────── */

  function applyAnswer(state, qId, answerId) {
    const q = state.config.questions.find(q => q.id === qId);
    if (!q) return;
    const answer = q.answers.find(a => a.id === answerId);
    if (!answer) return;

    if (!state.poles) state.poles = { pro: 0, elite: 0 };

    if (state.answers[qId]) {
      const prev = q.answers.find(a => a.id === state.answers[qId]);
      if (prev) {
        CLUSTERS.forEach(c => {
          state.scores[c] = Math.max(0, state.scores[c] - (prev.scores[c] || 0));
          state.totalScore = Math.max(0, state.totalScore - (prev.scores[c] || 0));
        });
        const prevPoles = prev.poles || {};
        state.poles.pro   = Math.max(0, state.poles.pro   - (prevPoles.pro   || 0));
        state.poles.elite = Math.max(0, state.poles.elite - (prevPoles.elite || 0));
      }
    }

    state.answers[qId] = answerId;
    CLUSTERS.forEach(c => {
      const pts = answer.scores[c] || 0;
      state.scores[c] += pts;
      state.totalScore += pts;
    });
    // Poles are additive exactly like clusters. An answer with no `poles` key contributes 0/0, so a
    // legacy data file cannot throw here.
    const nextPoles = answer.poles || {};
    state.poles.pro   += (nextPoles.pro   || 0);
    state.poles.elite += (nextPoles.elite || 0);
  }

  // Route-aware "presented driver" (T-1a45d4). Computed AFTER the outcome is known so the
  // highlighted cluster on the results bars is ALWAYS consistent with the recommended product.
  // computeOutcome's routing is unchanged → the PRO/ELITE/ORIGINAL distribution is identical;
  // only the highlighted bar / pill can change (and only in the previously-contradictory cases).
  function computePresentedDriver(state) {
    var cfg     = state.config || {};
    var outcome = state.outcome;
    var route   = state.outcomeRoute;
    var scores  = state.scores;
    // v2: under two-pole the ruled taxonomy is overload -> ELITE (the over-stimulated pole) and
    // hormones -> neither (a life context never routes, so it can never be the "why this formula").
    // Every v2 data file states both sets explicitly; these are the fallbacks.
    var twoPole = isTwoPole(state);
    var ELITE_CLUSTERS = cfg.elite_clusters || (twoPole ? ['stress', 'overload'] : ['stress', 'hormones']);
    var PRO_CLUSTERS   = cfg.pro_clusters   || (twoPole ? ['nutrition', 'sleep'] : ['sleep', 'nutrition', 'overload']);

    // 1) ORIGINAL (age gate) / BUNDLE (3-formula set — never a single-cluster story)
    //    + severity-driven routes don't claim a "primary driver" cluster; frame via copy instead (§2.4).
    if (outcome === 'ORIGINAL' || outcome === 'BUNDLE' || route === 'forced'
        || route === 'severity_high' || route === 'severity_low'
        || route === 'both_high' || route === 'low_signal' || route === 'no_lead') {
      state.presentedDriver = null;
      state.presentedDriverMode = (outcome === 'ORIGINAL' || outcome === 'BUNDLE' || route === 'forced') ? 'suitability' : 'overall_load';
      return;
    }

    // 2) Otherwise highlight the highest-scoring cluster CONSISTENT with the recommended product.
    var consistent = (outcome === 'ELITE') ? ELITE_CLUSTERS : PRO_CLUSTERS;
    var best = null, bestScore = -1;
    CLUSTERS.forEach(function (c) {
      if (consistent.indexOf(c) !== -1 && scores[c] > bestScore) { best = c; bestScore = scores[c]; }
    });
    // 3) Never tag a 0% bar (e.g. tied→ELITE where the tie was between two PRO clusters).
    state.presentedDriver = (bestScore > 0) ? best : null;
    state.presentedDriverMode = (state.presentedDriver) ? 'single' : 'balanced';
  }

  function computeOutcome(state) {
    routeOutcome(state);
    computePresentedDriver(state);
  }

  // Rank the five clusters. They drive the results bars, cause_flags and the driver pill on BOTH
   // paths; under two-pole they no longer influence the recommendation. Declared once so the two
   // routers cannot drift apart.
  function rankClusters(state) {
    const scores = state.scores;
    const sorted = CLUSTERS.slice().sort((a, b) => scores[b] - scores[a]);
    state.dominantCluster = sorted[0];
    state.secondaryCluster = sorted[1];
  }

  function isTwoPole(state) {
    return !!(state && state.config && state.config.scoring
              && state.config.scoring.method === 'two_pole');
  }

  // Collect the pinned outcome, if any. A SUITABILITY gate (ORIGINAL — ages 10-15 / sensitive)
  // always beats a PREFERENCE gate (BUNDLE — "try all three"): on the hp lanes the two live in
  // different questions and can co-occur, and a child must never be recommended the Bundle because
  // of an earlier answer. v1 got this right only incidentally, by question order.
  function collectForcedOutcome(state) {
    var forced = null;
    state.config.questions.forEach(function (q) {
      const aid = state.answers[q.id];
      if (!aid) return;
      const ans = q.answers.find(function (a) { return a.id === aid; });
      if (!ans || !ans.force_outcome) return;
      if (ans.force_outcome === 'ORIGINAL') { forced = 'ORIGINAL'; return; }
      if (forced !== 'ORIGINAL') forced = ans.force_outcome;
    });
    return forced;
  }

  // v2 (2026-09-06). Two authored poles decide the recommendation; the total never does.
  //   forced      -> the pinned SKU (suitability beats preference)
  //   low_signal  -> too little signal on either pole            -> Starter Bundle (R2)
  //   both_high   -> BOTH poles clear the bar AND neither leads by the margin -> Starter Bundle
  //                  (D3 as RULED 2026-09-09, T-2ef648: both_high only when margin < lead_min_margin.
  //                  The ceiling IS lead_min_margin, so both_high and pole_lead tile the both-clear
  //                  region with no third constant and no dead band; a both-high pair with a clear
  //                  lead is routed to its LEADING pole. From 2026-09-06 to 2026-09-09 both_high
  //                  fired regardless of margin and won ahead of the lead.)
  //   pole_lead   -> one pole leads by the margin (margin >= lead_min_margin) -> PRO or ELITE
  //   no_lead     -> mixed, neither clears the bar, neither leads -> Starter Bundle
  function routeOutcomeTwoPole(state) {
    const tp = (state.config.scoring && state.config.scoring.two_pole) || {};
    const lowSignalTotal = (tp.low_signal_total_pts != null) ? tp.low_signal_total_pts : 3;
    const poleHigh       = (tp.pole_high_pts != null) ? tp.pole_high_pts : 6;
    const leadMargin     = (tp.lead_min_margin != null) ? tp.lead_min_margin : 0.20;
    const poles = state.poles || { pro: 0, elite: 0 };
    const P = poles.pro || 0;
    const E = poles.elite || 0;

    rankClusters(state);

    const forced = collectForcedOutcome(state);
    if (forced) {
      state.outcomeRoute = 'forced';
      state.outcome = coerceLegacyBundle(forced, state);
      return;
    }

    const T = P + E;
    if (T < lowSignalTotal) {
      state.outcomeRoute = 'low_signal';
      state.outcome = coerceLegacyBundle('BUNDLE', state);
      return;
    }
    // T is > 0 here (lowSignalTotal is >= 1 in every shipped config), but the guard is kept so the
    // margin can never be NaN if a config ever sets the floor to 0.
    const margin = (T === 0) ? 0 : Math.abs(P - E) / T;
    if (Math.min(P, E) >= poleHigh && margin < leadMargin) {
      state.outcomeRoute = 'both_high';
      state.outcome = coerceLegacyBundle('BUNDLE', state);
      return;
    }
    if (margin >= leadMargin) {
      state.outcomeRoute = 'pole_lead';
      state.outcome = (P > E) ? 'PRO' : 'ELITE';
      return;
    }
    state.outcomeRoute = 'no_lead';
    state.outcome = coerceLegacyBundle('BUNDLE', state);
  }

  function routeOutcome(state) {
    if (isTwoPole(state)) { routeOutcomeTwoPole(state); return; }
    routeOutcomeLegacy(state);
  }

  // v1 routing, unchanged. Reached by any config without scoring.method === 'two_pole' — retired
  // b/c/gd variants, and (during CDN propagation) a not-yet-updated lane file.
  function routeOutcomeLegacy(state) {
    const cfg = state.config.scoring;
    const thresholds = cfg.thresholds;
    const total = state.totalScore;
    const scores = state.scores;

    if (total === 0) {
      state.outcomeRoute = 'zero';
      state.outcome = 'PRO';
      state.dominantCluster = 'overload';
      state.secondaryCluster = 'nutrition';
      return;
    }

    const sorted = CLUSTERS.slice().sort((a, b) => scores[b] - scores[a]);
    const dominant = sorted[0];
    const secondary = sorted[1];

    state.dominantCluster = dominant;
    state.secondaryCluster = secondary;

    var forced = collectForcedOutcome(state);
    if (forced) {
      state.outcomeRoute = 'forced';
      state.outcome = coerceLegacyBundle(forced, state);
      return;
    }

    const dominantPct = scores[dominant] / total;
    const secondaryPct = scores[secondary] / total;

    var outcomes = (state.config.outcomes) || {};

    if (total >= thresholds.severity_high_pts) {
      state.outcomeRoute = 'severity_high';
      state.outcome = coerceLegacyBundle(outcomes.severity_high || 'ELITE', state);
      return;
    }
    if (total <= thresholds.severity_low_pts) {
      state.outcomeRoute = 'severity_low';
      state.outcome = coerceLegacyBundle(outcomes.severity_low || 'ORIGINAL', state);
      return;
    }

    if (Math.abs(dominantPct - secondaryPct) <= thresholds.tie_within_pct) {
      state.outcomeRoute = 'tied';
      state.outcome = coerceLegacyBundle(outcomes.tied || 'ELITE', state);
      return;
    }

    if (dominant === 'hormones' && dominantPct >= thresholds.dominant_hormones_pct) {
      state.outcomeRoute = 'dominant';
      state.outcome = 'ELITE';
      return;
    }

    if (dominantPct >= thresholds.dominant_single_pct) {
      state.outcomeRoute = 'dominant';
      state.outcome = (dominant === 'stress') ? 'ELITE' : 'PRO';
      return;
    }

    if (secondary === 'stress' && secondaryPct >= thresholds.dominant_single_pct * 0.75) {
      state.outcomeRoute = 'secondary';
      state.outcome = 'ELITE';
    } else {
      state.outcomeRoute = 'fallback';
      state.outcome = 'PRO';
    }
  }

  /* ─────────────────────────────────────────────
     UTM HELPERS
  ───────────────────────────────────────────── */

  function captureUTMParams() {
    const params = {};
    const search = new URLSearchParams(window.location.search);
    const utmKeys = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term',
      'utm_content', 'utm_id', 'utm_source_platform',
      'utm_creative_format', 'utm_marketing_tactic',
      'bz_wave', 'bz_lp', 'bz_product', 'bz_test',
      'bz_compliance', 'bz_creative',
    ];
    utmKeys.forEach(k => {
      if (search.has(k)) params[k] = search.get(k);
    });
    return params;
  }

  // T-3bbb8c (2026-09-08) introduced a paid-destination override here: on an ad-tagged arrival a
  // BUNDLE outcome bought FOCUS PRO™ with the bundle one click away, under the 13 Aug 2026 "never the
  // advertised CTA" rule. Calum REVERSED it on 2026-09-09 (T-b49e7c, alongside the rule relaxation
  // T-d5717a): "if the quiz ended up on the starter bundle, then that's what they should be getting" —
  // quiz results follow the QUIZ only, never ad tagging. The buy block is therefore the outcome's own
  // template on every route and for every session, paid or organic. isPaidSession stays because the
  // relay payload's bz_paid still reports it; bz_cta now equals the outcome SKU, which is what lets
  // the readout prove the served engine carries the reversal.
  // Paid = inbound ad tagging, decided from state.utmParams. Observed 2026-09-08 over 147 real
  // completers: every paid row carries utm_medium=cpc and bz_wave is exclusive to paid; the wider
  // medium list is anticipatory so a new campaign medium never silently reads organic.
  const PAID_MEDIUMS = ['cpc', 'ppc', 'paid', 'paid_social', 'paidsocial', 'paid-social', 'display',
    'pmax', 'performance_max', 'demand_gen', 'dg', 'paid_search'];

  function isPaidSession(utm) {
    utm = utm || {};
    if (typeof utm.bz_wave === 'string' && utm.bz_wave.trim() !== '') return true;
    const medium = String(utm.utm_medium || '').toLowerCase().trim();
    return PAID_MEDIUMS.indexOf(medium) !== -1;
  }

  // ONE resolver for the buy block. Price, primary CTA, the relay payload's bz_cta and the
  // completed-event's cta_sku all read THIS object and nothing else. Since T-b49e7c (Calum
  // 2026-09-09) it has exactly one shape — the outcome's own template — regardless of route, lane
  // note or session tagging. `override` / `note` / `alt` are kept on the object (always false / ''
  // / null) so the renderer, tools/_quiz_cta_fixture.js and the live walks keep asserting that no
  // override is ever rendered; a lane file's paid_cta_note is inert and no longer read here.
  function resolveCTA(state) {
    const sku = coerceLegacyBundle(state.outcome, state);
    const meta = SKU_META[sku] || {};
    const tpl = selectResultTemplate(state);
    return {
      sku: sku,
      tpl: tpl,
      url: buildCheckoutURL(state),
      label: tpl.cta_primary || ('Explore ' + (meta.name || '')),
      price: tpl.price || meta.price || '',
      override: false,
      note: '',
      alt: null,
    };
  }

  function buildCheckoutURL(state, skuOverride) {
    const outcomeSku = coerceLegacyBundle(state.outcome, state);
    // T-3bbb8c (2026-09-08): the optional second argument builds the URL for a product OTHER than
    // the outcome. When it differs from the outcome, bz_rec carries the recommendation so downstream
    // reports can see it. Since T-b49e7c (2026-09-09) no production caller passes it — resolveCTA
    // always buys the outcome — so live URLs never carry bz_rec; the argument stays for the fixture.
    // No-arg callers produce byte-for-byte the same URL as before.
    const sku = skuOverride || outcomeSku;
    const meta = SKU_META[sku];
    // 2026-05-28: per-market storefront override. Data file (config.storefront_base)
    // wins over the shared SHOPIFY_BASE fallback. Lets US/DE/FR results route to
    // their own storefront domain. JS itself remains identical across all 4 markets.
    // Backward-compatible: quizzes that don't set storefront_base in cfg keep
    // the existing brainzyme.com behaviour.
    const storefront = getCfg(state, 'storefront_base', SHOPIFY_BASE);
    if (!meta) return storefront;

    // 2026-07-17: per-market PDP path override — data file (result_templates[sku].url)
    // wins over the shared SKU_META slug, same pattern as the image_url (2026-05-20)
    // and price (2026-05-28) overrides. Fixes per-market handles (e.g. FR localized
    // slugs, DE brainzyme-combo) that the shared UK slug can't represent.
    const tpls = (state.config && state.config.result_templates) || {};
    const tpl = (skuOverride && skuOverride !== outcomeSku) ? (tpls[sku] || {}) : selectResultTemplate(state);
    const base = storefront + (tpl.url || meta.url_slug);
    const p = new URLSearchParams();

    Object.entries(state.utmParams || {}).forEach(([k, v]) => p.set(k, v));
    p.set('bz_test', state.config.utm_test_param);
    p.set('bz_lp', 'quiz');
    p.set('bz_product', sku.toLowerCase());
    if (skuOverride && sku !== outcomeSku) p.set('bz_rec', outcomeSku.toLowerCase());

    return base + '?' + p.toString();
  }

  /* ─────────────────────────────────────────────
     LOCALSTORAGE
  ───────────────────────────────────────────── */

  function storageKey(variantId, market) {
    return 'bz_quiz_' + variantId + '_' + market;
  }

  function saveProgress(state) {
    try {
      const key = storageKey(state.config.variant, state.config.market);
      const payload = {
        // Bumped to 2 for the two-pole engine. loadProgress DISCARDS anything older rather than
        // resuming a session whose totals were computed under different rules.
        schema:       2,
        variantId:    state.config.variant,
        market:       state.config.market,
        answers:      state.answers,
        scores:       state.scores,
        totalScore:   state.totalScore,
        poles:        state.poles,
        currentPhaseIndex: state.currentPhaseIndex,
        currentQuestionIndexInPhase: state.currentQuestionIndexInPhase,
        globalQuestionIndex: state.globalQuestionIndex,
        emailSubmitted: state.emailSubmitted,
        outcome:      state.outcome,
        outcomeRoute: state.outcomeRoute,
        dominantCluster: state.dominantCluster,
        secondaryCluster: state.secondaryCluster,
        utmParams:    state.utmParams,
        savedAt:      Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (_) {}
  }

  function loadProgress(variantId, market) {
    try {
      const key = storageKey(variantId, market);
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.savedAt > 86400000) {
        localStorage.removeItem(key);
        return null;
      }
      // A blob written by the v1 engine has no poles and its outcome was decided by rules that no
      // longer exist. Restarting is the honest option; resuming would silently carry a v1
      // recommendation into a v2 result screen.
      if (data.schema !== 2) {
        localStorage.removeItem(key);
        return null;
      }
      return data;
    } catch (_) {
      return null;
    }
  }

  function clearProgress(variantId, market) {
    try {
      localStorage.removeItem(storageKey(variantId, market));
    } catch (_) {}
  }

  function stampVariantAssignment(variantId, market) {
    try {
      localStorage.setItem(
        'bz_quiz_variant_' + market,
        JSON.stringify({ variantId, stampedAt: Date.now() })
      );
    } catch (_) {}
  }

  /* ─────────────────────────────────────────────
     TEMPLATE SUBSTITUTION
  ───────────────────────────────────────────── */

  // The bundle is reached by four different routes and they are not the same message. One selector,
  // used by the renderer, the token resolver and the checkout URL builder, so a card can never be
  // rendered from one template while its price or link come from another.
  //   forced     -> BUNDLE_FORCED       ("you asked to try all three")
  //   both_high  -> BUNDLE_BOTH_HIGH    ("both patterns showed up clearly")
  //   low_signal -> BUNDLE_LOW_SIGNAL   ("nothing points strongly either way yet")
  //   no_lead    -> BUNDLE_LOW_SIGNAL   (same message: no clear direction)
  // Each falls back to the neutral base BUNDLE template, which is also what a cached v1 engine
  // renders when it meets a v2 data file — so that copy must be true of any bundle result.
  function selectResultTemplate(state) {
    const tpls = (state && state.config && state.config.result_templates) || {};
    const sku = coerceLegacyBundle(state.outcome, state);
    if (sku !== 'BUNDLE') return tpls[sku] || {};
    const route = state.outcomeRoute;
    const key = (route === 'forced') ? 'BUNDLE_FORCED'
              : (route === 'both_high') ? 'BUNDLE_BOTH_HIGH'
              : (route === 'low_signal' || route === 'no_lead') ? 'BUNDLE_LOW_SIGNAL'
              : null;
    return (key && tpls[key]) || tpls.BUNDLE || {};
  }

  function resolveTemplate(template, state) {
    if (!template || typeof template !== 'string') return '';
    const sku = coerceLegacyBundle(state.outcome, state);
    const meta = SKU_META[sku] || {};
    const dc = state.dominantCluster;
    const sc = state.secondaryCluster;
    const tpl = selectResultTemplate(state);
    const checkoutURL = buildCheckoutURL(state);

    const domPhrase = clusterCopyPhrase(dc, 'phrase', state);
    const secPhrase = clusterCopyPhrase(sc, 'phrase', state);

    const tokens = {
      '[CLUSTER_1_NAME]':    getClusterLabel(state, dc) || dc,
      '[CLUSTER_2_NAME]':    getClusterLabel(state, sc) || sc,
      '[CLUSTER_1_LABEL]':   getClusterLabel(state, dc) || dc,
      '[CLUSTER_2_LABEL]':   getClusterLabel(state, sc) || sc,
      '[CLUSTER_1_PHRASE]':  domPhrase,
      '[CLUSTER_2_PHRASE]':  secPhrase,
      '[CLUSTER_1_SHORT]':   clusterCopyPhrase(dc, 'short', state),
      '[CLUSTER_2_SHORT]':   clusterCopyPhrase(sc, 'short', state),
      '[SKU]':               sku === 'BUNDLE' ? 'Starter Bundle' : sku,
      '[CLASS_2_BENEFIT]':   meta.class2 || '',
      '[CLASS_3_MECHANISM]': meta.class3 || '',
      '[INGREDIENT_COUNT]':  meta.ingredient_count > 0 ? String(meta.ingredient_count) : '',
      '[PRICE]':             tpl.price || meta.price || '',
      '[PRODUCT_NAME]':      tpl.name || meta.name || '',
      '[PRODUCT_URL]':       checkoutURL,
      '[DOMINANT_CLUSTER_PHRASE]': domPhrase,
      '[HIGH_SEVERITY_ADDITION]':  '',
    };

    // 2026-07-29 (T-3cea72): the cluster PHRASE values are lowercase MID-sentence
    // fragments by design ("your brain fog is being worsened by poor sleep..."), but at
    // least one result template places [CLUSTER_2_PHRASE] straight after a full stop. A
    // real quiz run rendered "...supports stress-free focus and positive mood. your brain
    // fog is being worsened by..." to a customer — lowercase after a period.
    //
    // Fixed in the ENGINE, not the one offending template: the same join is reachable
    // from every branch, and each market's data file supplies its own phrases via
    // cluster_phrases, so a per-template patch would leave the trap armed for the next one.
    //
    // The obvious implementation — regex-capitalising any lowercase letter after ". " —
    // is WRONG and I verified it before shipping: it turns "e.g. with food" into
    // "e.g. With food". So this only ever raises the FIRST letter of a substituted TOKEN
    // VALUE, and only where the TEMPLATE itself puts that token at a sentence start.
    // No other text in the string can be touched.
    const SENTENCE_START = /([.!?]["')\]]?\s+)$/;
    let out = template;
    Object.entries(tokens).forEach(([token, value]) => {
      if (!value || typeof value !== 'string') {
        out = out.split(token).join(value == null ? '' : value);
        return;
      }
      const capped = value.charAt(0).toUpperCase() + value.slice(1);
      let idx = out.indexOf(token);
      while (idx !== -1) {
        const before = out.slice(0, idx);
        const useCap = SENTENCE_START.test(before) || before === '';
        out = before + (useCap ? capped : value) + out.slice(idx + token.length);
        idx = out.indexOf(token, idx + (useCap ? capped.length : value.length));
      }
    });
    return out;
  }

  // 2026-05-24 (T-5389b7 follow-up): clusterCopyPhrase now accepts `state` so
  // DE/FR data files can supply localized cluster phrases via
  //   "cluster_phrases": { "<cluster>": { "phrase": "...", "short": "..." } }
  // English fallbacks preserved. Engine identical across markets.
  function clusterCopyPhrase(cluster, mode, state) {
    const cfgPhrases = state && state.config && state.config.cluster_phrases;
    if (cfgPhrases && cfgPhrases[cluster] && cfgPhrases[cluster][mode]) {
      return cfgPhrases[cluster][mode];
    }
    const phrases = {
      sleep:     { phrase: 'your brain fog is being worsened by poor sleep and recovery', short: 'sleep & recovery' },
      stress:    { phrase: 'stress and mental overwhelm are driving your fog', short: 'stress & mood' },
      nutrition: { phrase: 'energy gaps and nutritional depletion are likely contributors', short: 'nutrition & energy' },
      hormones:  { phrase: 'hormonal changes and life-stage factors are a key driver', short: 'hormones & life stage' },
      overload:  { phrase: 'cognitive overload and attention demands are depleting your focus', short: 'overload & attention' },
    };
    const p = phrases[cluster];
    if (!p) return cluster || '';
    return mode === 'short' ? p.short : p.phrase;
  }

  /* ─────────────────────────────────────────────
     ACCESSIBILITY HELPERS
  ───────────────────────────────────────────── */

  function announceToSR(message, container) {
    let live = container.querySelector('[data-bz-sr-live]');
    if (!live) {
      live = document.createElement('div');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      live.setAttribute('data-bz-sr-live', '1');
      live.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
      container.appendChild(live);
    }
    live.textContent = '';
    requestAnimationFrame(() => { live.textContent = message; });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ─────────────────────────────────────────────
     RENDER — QUESTION SCREEN
  ───────────────────────────────────────────── */

  function renderQuestionScreen(state, container, onAnswer, options = {}) {
    const phase = state.config.phases[state.currentPhaseIndex];
    const qId = phase.question_ids[state.currentQuestionIndexInPhase];
    const question = state.config.questions.find(q => q.id === qId);
    if (!question) return;

    const qNum = state.globalQuestionIndex + 1;
    const total = state.totalQuestions;
    const progressPct = Math.round((qNum - 1) / total * 100);
    const existingAnswer = state.answers[qId] || null;

    // Progress counter — pulls template from config (per-market localised),
    // falls back to EN if data file pre-dates the field.
    const counterTpl = state.config.progress_counter_template || 'Question {N} of ' + total;
    const counterText = counterTpl
      .split('{N}').join(String(qNum))
      .split('{TOTAL}').join(String(total));

    // Inline phase header — only on the first question of each phase, replaces
    // the removed interstitial screen. Small heading above the question.
    const isPhaseStart = state.currentQuestionIndexInPhase === 0;

    container.innerHTML = [
      '<div class="bz-quiz__progress" role="progressbar"',
      '  aria-valuenow="' + progressPct + '" aria-valuemin="0" aria-valuemax="100"',
      '  aria-label="Quiz progress: ' + escHtml(counterText) + '">',
      '  <div class="bz-quiz__progress-track">',
      '    <div class="bz-quiz__progress-bar" style="width:' + progressPct + '%"></div>',
      '  </div>',
      '  <div class="bz-quiz__progress-label">',
      '    <span class="bz-quiz__phase-name">' + escHtml(phase.label) + '</span>',
      '    <span class="bz-quiz__q-counter">' + escHtml(counterText) + '</span>',
      '  </div>',
      '</div>',
      '<div class="bz-quiz__question-wrap" data-qid="' + escHtml(qId) + '">',
      isPhaseStart ? '  <h2 class="bz-quiz__phase-header">' + escHtml(phase.label) + '</h2>' : '',
      '  <p class="bz-quiz__q-text" id="bz-question-label">' + escHtml(question.text) + '</p>',
      '  <div class="bz-quiz__answers" role="group" aria-labelledby="bz-question-label">',
      question.answers.map(function(a, i) {
        var sel = existingAnswer === a.id;
        return [
          '<button',
          '  class="bz-quiz__answer' + (sel ? ' bz-quiz__answer--selected' : '') + '"',
          '  data-answer-id="' + escHtml(a.id) + '"',
          '  data-qid="' + escHtml(qId) + '"',
          '  aria-pressed="' + (sel ? 'true' : 'false') + '"',
          '  tabindex="' + (i === 0 ? '0' : '-1') + '">',
          '  <span class="bz-quiz__answer-check" aria-hidden="true"></span>',
          '  <span class="bz-quiz__answer-label">' + escHtml(a.label) + '</span>',
          '</button>',
        ].join('');
      }).join(''),
      '  </div>',
      '</div>',
    ].join('');

    container.setAttribute('data-screen', 'question');

    const answerBtns = container.querySelectorAll('.bz-quiz__answer');
    answerBtns.forEach(function(btn, i) {
      btn.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          var next = answerBtns[i + 1];
          if (next) { next.tabIndex = 0; btn.tabIndex = -1; next.focus(); }
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var prev = answerBtns[i - 1];
          if (prev) { prev.tabIndex = 0; btn.tabIndex = -1; prev.focus(); }
        } else if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          btn.click();
        }
      });

      btn.addEventListener('click', function() {
        answerBtns.forEach(function(b) {
          b.classList.remove('bz-quiz__answer--selected');
          b.setAttribute('aria-pressed', 'false');
          b.tabIndex = -1;
        });
        btn.classList.add('bz-quiz__answer--selected');
        btn.setAttribute('aria-pressed', 'true');
        btn.tabIndex = 0;

        var answerId = btn.getAttribute('data-answer-id');
        announceToSR('Selected: ' + btn.querySelector('.bz-quiz__answer-label').textContent, container);

        var delay = prefersReducedMotion() ? 50 : 400;
        setTimeout(function() { onAnswer(qId, answerId); }, delay);
      });
    });

    var selectedBtn = container.querySelector('.bz-quiz__answer--selected');
    if (selectedBtn) {
      if (options.focus) selectedBtn.focus();
    } else if (answerBtns[0]) {
      answerBtns[0].tabIndex = 0;
      if (options.focus) answerBtns[0].focus();
    }
  }

  /* ─────────────────────────────────────────────
     RENDER — PHASE COMPLETE INTERSTITIAL
  ───────────────────────────────────────────── */

  function renderPhaseComplete(state, container, onContinue) {
    const phase = state.config.phases[state.currentPhaseIndex];
    const nextPhaseIndex = state.currentPhaseIndex + 1;
    const isLastPhase = nextPhaseIndex >= state.config.phases.length;

    container.innerHTML = [
      '<div class="bz-quiz__interstitial" role="region" aria-label="Phase complete">',
      '  <div class="bz-quiz__interstitial-icon" aria-hidden="true">',
      '    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">',
      '      <circle cx="24" cy="24" r="22" stroke="var(--bz-gold)" stroke-width="2.5" fill="none" opacity=".25"/>',
      '      <path d="M14 24.5L21 31.5L34 17" stroke="var(--bz-gold)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>',
      '    </svg>',
      '  </div>',
      '  <h2 class="bz-quiz__interstitial-heading">' + escHtml(phase.label) + ' — done</h2>',
      '  <p class="bz-quiz__interstitial-copy">' + escHtml(phase.interstitial_copy) + '</p>',
      !isLastPhase ? '<p class="bz-quiz__interstitial-next">Next: ' + escHtml(state.config.phases[nextPhaseIndex].label) + '</p>' : '',
      '  <button class="bz-quiz__btn bz-quiz__btn--primary bz-quiz__continue" type="button">',
      '    ' + (isLastPhase ? 'See my Brain Fog Profile' : 'Continue') + ' <span aria-hidden="true"> →</span>',
      '  </button>',
      '</div>',
    ].join('');

    container.setAttribute('data-screen', 'phase-complete');
    const btn = container.querySelector('.bz-quiz__continue');
    if (btn) { btn.focus(); btn.addEventListener('click', onContinue); }
  }

  /* ─────────────────────────────────────────────
     RENDER — PROCESSING SCREEN (Noom-style)
  ───────────────────────────────────────────── */

  function renderProcessingScreen(state, container, onComplete) {
    const cfg = state.config.processing_screen;
    const duration = cfg.duration_ms || 7000;
    // 2026-05-24 (T-5389b7): per-market override via cfg.processing_screen.copy_stages array.
    const copyStages = (cfg && Array.isArray(cfg.copy_stages) && cfg.copy_stages.length)
      ? cfg.copy_stages
      : [
          'Analysing your answers…',
          'Mapping your symptom patterns…',
          'Identifying likely root causes…',
          'Matching to your Brain Fog profile…',
          'Building your personalised result…',
        ];

    container.innerHTML = [
      '<div class="bz-quiz__processing" role="region" aria-label="Calculating your Brain Fog Profile" aria-live="polite">',
      '  <div class="bz-quiz__processing-spinner" aria-hidden="true">',
      '    <svg class="bz-quiz__spinner-svg" viewBox="0 0 50 50">',
      '      <circle class="bz-quiz__spinner-track" cx="25" cy="25" r="20" fill="none" stroke-width="3"/>',
      '      <circle class="bz-quiz__spinner-arc" cx="25" cy="25" r="20" fill="none" stroke-width="3" stroke-linecap="round"/>',
      '    </svg>',
      '  </div>',
      '  <div class="bz-quiz__processing-copy" id="bz-processing-copy" aria-live="polite">' + escHtml(copyStages[0]) + '</div>',
      '  <div class="bz-quiz__processing-bar-wrap" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" id="bz-processing-bar-aria">',
      '    <div class="bz-quiz__processing-bar" id="bz-processing-bar"></div>',
      '  </div>',
      '</div>',
    ].join('');

    container.setAttribute('data-screen', 'processing');

    const copyEl = container.querySelector('#bz-processing-copy');
    const barEl  = container.querySelector('#bz-processing-bar');
    const barAria = container.querySelector('#bz-processing-bar-aria');

    if (prefersReducedMotion()) {
      setTimeout(onComplete, 800);
      return;
    }

    let elapsed = 0;
    const interval = 60;
    const stageInterval = Math.floor(duration / copyStages.length);
    let stageIdx = 0;

    const tick = setInterval(function() {
      elapsed += interval;
      const pct = Math.min(100, Math.round(elapsed / duration * 100));
      barEl.style.width = pct + '%';
      barAria.setAttribute('aria-valuenow', pct);

      const expectedStage = Math.floor(elapsed / stageInterval);
      if (expectedStage > stageIdx && expectedStage < copyStages.length) {
        stageIdx = expectedStage;
        copyEl.textContent = copyStages[stageIdx];
      }

      if (elapsed >= duration) {
        clearInterval(tick);
        onComplete();
      }
    }, interval);
  }

  /* ─────────────────────────────────────────────
     RENDER — EMAIL GATE
  ───────────────────────────────────────────── */

  function renderEmailGate(state, container, onSubmit, onSkip) {
    const emailCfg = state.config.email_gate_copy.pattern_2;
    const firstNameLabel       = getCfg(state, 'email_first_name_label', 'First name');
    const firstNamePlaceholder = getCfg(state, 'email_first_name_placeholder', 'Your first name');
    const emailLabel           = getCfg(state, 'email_email_label', 'Email address');
    const emailPlaceholder     = getCfg(state, 'email_email_placeholder', 'your@email.com');
    const skipLabel            = getCfg(state, 'email_skip_label', 'Skip — show my result');
    const legalHtml            = getCfg(state, 'email_legal_html', 'No spam. Unsubscribe any time. By submitting you agree to our <a href="https://www.brainzyme.com/pages/privacy-policy" target="_blank" rel="noopener">privacy policy</a>.');

    container.innerHTML = [
      '<div class="bz-quiz__email-gate" role="region" aria-label="Brain Fog Profile email">',
      '  <div class="bz-quiz__email-gate-inner">',
      '    <h2 class="bz-quiz__email-heading">' + escHtml(emailCfg.headline) + '</h2>',
      '    <p class="bz-quiz__email-sub">' + escHtml(emailCfg.sub_copy) + '</p>',
      '    <form class="bz-quiz__email-form" id="bz-email-form" novalidate>',
      '      <div class="bz-quiz__email-fields">',
      '        <div class="bz-quiz__field-group">',
      '          <label class="bz-quiz__field-label" for="bz-first-name">' + escHtml(firstNameLabel) + '</label>',
      '          <input class="bz-quiz__field-input" id="bz-first-name" name="firstName"',
      '            type="text" autocomplete="given-name" placeholder="' + escHtml(firstNamePlaceholder) + '" aria-required="false"/>',
      '        </div>',
      '        <div class="bz-quiz__field-group">',
      '          <label class="bz-quiz__field-label" for="bz-email">' + escHtml(emailLabel) + '</label>',
      '          <input class="bz-quiz__field-input" id="bz-email" name="email"',
      '            type="email" autocomplete="email" placeholder="' + escHtml(emailPlaceholder) + '"',
      '            aria-required="true" aria-describedby="bz-email-error"/>',
      '          <span class="bz-quiz__field-error" id="bz-email-error" role="alert" aria-live="polite"></span>',
      '        </div>',
      '      </div>',
      '      <button class="bz-quiz__btn bz-quiz__btn--primary bz-quiz__btn--full" type="submit">',
      '        ' + escHtml(emailCfg.cta),
      '      </button>',
      '    </form>',
      '    <button class="bz-quiz__skip-link" type="button" id="bz-email-skip">',
      '      ' + escHtml(skipLabel),
      '    </button>',
      '    <p class="bz-quiz__email-legal">',
      '      ' + legalHtml,
      '    </p>',
      '  </div>',
      '</div>',
    ].join('');

    container.setAttribute('data-screen', 'email-gate');

    const form = container.querySelector('#bz-email-form');
    const skipBtn = container.querySelector('#bz-email-skip');
    const emailInput = container.querySelector('#bz-email');
    const errorEl = container.querySelector('#bz-email-error');

    emailInput.focus();

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      const emailVal = emailInput.value.trim();
      const firstNameVal = container.querySelector('#bz-first-name').value.trim();

      if (!isValidEmail(emailVal)) {
        errorEl.textContent = getCfg(state, 'email_validation_error', 'Please enter a valid email address.');
        emailInput.focus();
        return;
      }

      errorEl.textContent = '';
      onSubmit(emailVal, firstNameVal);
    });

    if (skipBtn) {
      skipBtn.addEventListener('click', function() { onSkip(); });
    }
  }

  /* ─────────────────────────────────────────────
     RENDER — RESULTS PAGE
  ───────────────────────────────────────────── */

  function renderResultsPage(state, container, onCTAClick, options = {}) {
    const sku = coerceLegacyBundle(state.outcome, state);
    const meta = SKU_META[sku] || {};
    const tpl = selectResultTemplate(state);
    // T-3bbb8c (2026-09-08): the BUY block (price, primary CTA, note, alt link) comes from ONE
    // resolver. The recommendation block above it still renders the outcome's own template.
    const cta = resolveCTA(state);
    const checkoutURL = cta.url;
    // 2026-07-17: per-market product-name override (same pattern as image_url/price).
    const productName = (tpl && tpl.name) || meta.name || '';

    const openingText   = resolveTemplate(tpl.opening, state);
    // Tied-path: append the PRO-alternative sentence (`reasoning_tied`) from
    // the per-market data file. Falls back to plain `reasoning` if route is
    // not tied or the field is missing. Keeps localised copy out of the engine.
    const baseReasoning = tpl.reasoning || '';
    const tiedExtra = (state.outcomeRoute === 'tied' && tpl.reasoning_tied)
      ? '\n' + tpl.reasoning_tied
      : '';
    const reasoningText = resolveTemplate(baseReasoning + tiedExtra, state);
    const ctaPrimary    = resolveTemplate(cta.label, state);
    const ctaAltLabel   = cta.alt ? resolveTemplate(cta.alt.label, state) : '';

    const resultsBadgeLabel    = getCfg(state, 'results_badge_label', 'Your Brain Fog Profile');
    const resultsHeadline      = getCfg(state, 'results_headline', 'Your results are ready');
    const causeHeading         = getCfg(state, 'cause_heading', 'What\'s behind your brain fog');
    const productRecommendedLabel = getCfg(state, 'product_recommended_label', 'Recommended for you');
    const trustReviewsText     = getCfg(state, 'trust_reviews_text', 'from 6,020+ verified reviews');
    const trustMedicalText     = getCfg(state, 'trust_medical_text', 'Trusted by 2,000+ medical professionals');
    const trustExtrasText      = getCfg(state, 'trust_extras_text', '365-day returns &middot; Made in Scotland &middot; Plant-powered');
    const priceSubText         = getCfg(state, 'price_sub_text', 'Free UK delivery &middot; Subscribe &amp; save 16–33%');
    const returnsNoteText      = getCfg(state, 'returns_note_text', '365-day no-quibble returns');

    container.innerHTML = [
      '<div class="bz-quiz__results" role="main">',
      '  <div class="bz-quiz__results-header">',
      '    <div class="bz-quiz__results-badge">' + escHtml(resultsBadgeLabel) + '</div>',
      '    <h1 class="bz-quiz__results-headline">' + escHtml(resultsHeadline) + '</h1>',
      '  </div>',

      '  <div class="bz-quiz__cause-reveal" aria-label="Root cause analysis">',
      '    <h2 class="bz-quiz__cause-heading">' + escHtml(causeHeading) + '</h2>',
      '    <div class="bz-quiz__cause-opening">' + paragraphsFromText(openingText) + '</div>',
      '    <div class="bz-quiz__clusters-grid" aria-label="Cause cluster scores">',
      renderClusterBars(state),
      '    </div>',
      '  </div>',

      '  <div class="bz-quiz__reasoning-block" aria-label="Why this formula">',
      '    <div class="bz-quiz__product-rec">',
      '      <div class="bz-quiz__product-badge">' + escHtml(productRecommendedLabel) + '</div>',
      (function(){
        // 2026-05-20: per-market image override. Data file (config.result_templates[sku].image_url)
        // wins over the shared SKU_META fallback. Lets DE/FR show DE/FR-labeled packaging
        // while UK/US use the ENG label. JS itself remains identical across all 4 markets.
        var imgUrl = (tpl && tpl.image_url) || meta.image_url;
        return imgUrl
          ? '<div class="bz-quiz__product-image"><img src="' + escHtml(imgUrl) + '" alt="' + escHtml(productName) + '" loading="lazy"></div>'
          : '';
      })(),
      '      <h2 class="bz-quiz__product-name">' + escHtml(productName) + '</h2>',
      '      <div class="bz-quiz__product-reasoning">' + paragraphsFromText(reasoningText) + '</div>',
      // D2 (Calum 2026-08-26): per-outcome trust strip. It was present in all 32 result templates
      // and the renderer never read it, so ELITE in particular had no per-outcome proof line.
      (function () {
        var strip = resolveTemplate(tpl.trust_strip || '', state);
        return strip
          ? '<p class="bz-quiz__price-sub bz-quiz__trust-strip">' + escHtml(strip) + '</p>'
          : '';
      })(),
      '    </div>',
      '  </div>',

      '  <div class="bz-quiz__trust-bar" aria-label="Trust signals">',
      '    <div class="bz-quiz__trust-item">',
      '      <span class="bz-quiz__trust-stars" aria-label="4.74 out of 5 stars">',
      '        <svg width="80" height="16" viewBox="0 0 80 16" aria-hidden="true" focusable="false">',
      renderStars(4.74),
      '        </svg>',
      '        <span class="bz-quiz__trust-rating">' + escHtml(getCfg(state, 'trust_rating_text', '4.74/5')) + '</span>',
      '      </span>',
      '      <span class="bz-quiz__trust-text">' + escHtml(trustReviewsText) + '</span>',
      '    </div>',
      '    <div class="bz-quiz__trust-divider" aria-hidden="true"></div>',
      '    <div class="bz-quiz__trust-item">',
      '      <span class="bz-quiz__trust-text">' + escHtml(trustMedicalText) + '</span>',
      '    </div>',
      '    <div class="bz-quiz__trust-divider" aria-hidden="true"></div>',
      '    <div class="bz-quiz__trust-item">',
      '      <span class="bz-quiz__trust-text">' + trustExtrasText + '</span>',
      '    </div>',
      '  </div>',

      '  <div class="bz-quiz__cta-block">',
      // T-3bbb8c: on a paid-tagged bundle result the note explains the PRO price BEFORE it is seen.
      cta.override
        ? '    <p class="bz-quiz__price-sub bz-quiz__paid-cta-note">' + escHtml(cta.note) + '</p>'
        : '',
      '    <div class="bz-quiz__price-reveal">',
      // 2026-05-28: per-market price override. Data file (config.result_templates[sku].price)
      // wins over the shared SKU_META.price (GBP) fallback. Same pattern as image_url
      // override above. Lets US/DE/FR show USD/EUR while UK keeps GBP. Backward-compatible.
      // T-3bbb8c: the price is the price of what the primary button buys (cta.price === the
      // outcome template's price unless the override is active).
      '      <span class="bz-quiz__price">' + escHtml(cta.price) + '</span>',
      '      <span class="bz-quiz__price-sub">' + priceSubText + '</span>',
      '    </div>',
      '    <a href="' + checkoutURL + '" class="bz-quiz__btn bz-quiz__btn--primary bz-quiz__btn--cta bz-quiz__btn--full"',
      '       id="bz-cta-primary" target="_blank" rel="noopener">',
      '      ' + escHtml(ctaPrimary),
      '    </a>',
      // T-b49e7c (2026-09-09): cta.alt is ALWAYS null now (the paid-CTA override is retired) — this
      // block is retained for shape only and never renders. Original T-3bbb8c intent, for the record:
      // the ruled outcome stays one click away. Plain navigation — deliberately NO click
      // handler, so it can never fall into the email-gate branch that #bz-cta-secondary uses.
      cta.alt
        ? '    <a id="bz-cta-bundle-alt" class="bz-quiz__cta-alt" href="' + cta.alt.url + '" target="_blank" rel="noopener">' + escHtml(ctaAltLabel) + '</a>'
        : '',
      // Secondary "Save my Brain Fog Profile" button removed 2026-05-19
      // (Umar feedback — non-functional UI, decision fatigue on results page).
      state.emailSubmitted
        ? '<p class="bz-quiz__email-confirmation">' + escHtml(state.config.email_confirmation_text || 'Your Brain Fog Recommendation has been sent — check your inbox.') + '</p>'
        : '',
      '    <p class="bz-quiz__returns-note">' + escHtml(returnsNoteText) + '</p>',
      '    <button id="bz-quiz-retake" class="bz-quiz__btn bz-quiz__btn--secondary bz-quiz__retake" type="button">' + escHtml(state.config.retake_cta_label || 'Take the quiz again') + '</button>',
      '  </div>',

      // 2026-05-20: Share/Copy link block removed (Umar feedback item 2.4).
      // Previously rendered a button that copied window.location.href — but
      // that URL is the generic quiz page, not the user's unique result.
      // Will return with A3 (unique result URLs) — separate workstream that
      // needs a worker route + KV schema. Until then, simpler to remove the
      // dishonest UI element entirely.
      // (Removed from results render array.)
      '</div>',
    ].join('');

    container.setAttribute('data-screen', 'results');

    const ctaPrimaryEl = container.querySelector('#bz-cta-primary');
    if (ctaPrimaryEl) {
      ctaPrimaryEl.addEventListener('click', function() { onCTAClick('primary', cta.sku, checkoutURL); });
    }
    const ctaSecondaryEl = container.querySelector('#bz-cta-secondary');
    if (ctaSecondaryEl) {
      ctaSecondaryEl.addEventListener('click', function() { onCTAClick('secondary', sku, null); });
    }
    const retakeEl = container.querySelector('#bz-quiz-retake');
    if (retakeEl) {
      retakeEl.addEventListener('click', function() { onCTAClick('retake', null, null); });
    }
    // 2026-05-20: Copy link handler removed (no element to bind — see render comment above).
    // The handler used to copy window.location.href, which is the generic quiz page URL,
    // not the user's result. Coming back with A3 (unique result URLs).

    if (!prefersReducedMotion()) {
      setTimeout(function() {
        container.querySelectorAll('.bz-quiz__cluster-fill').forEach(function(el) {
          // Force reflow so the transition starts from 0% and animates to target.
          // Without this, browsers may batch the style change with the initial
          // 0% paint and skip the transition entirely (Gemini QC LOW 1).
          void el.offsetWidth;
          el.style.width = el.getAttribute('data-target-width');
        });
      }, 100);
    } else {
      container.querySelectorAll('.bz-quiz__cluster-fill').forEach(function(el) {
        void el.offsetWidth;
        el.style.width = el.getAttribute('data-target-width');
      });
    }

    const h1 = container.querySelector('.bz-quiz__results-headline');
    if (h1 && options.focus) { h1.tabIndex = -1; h1.focus(); }
  }

  function renderClusterBars(state) {
    const scores = state.scores;
    const total  = state.totalScore || 1;

    const primaryDriverLabel = getCfg(state, 'cluster_primary_driver_label', 'Primary driver');
    // Outcome-aligned pill is OPT-IN per data file (T-1a45d4). A data file sets
    // "driver_pill_outcome_aligned": true ONLY once its result copy no longer names the raw
    // dominant cluster (the HP quizzes — no cluster tokens in prose). Until then the file keeps
    // the legacy raw-dominant pill, so no half-migrated quiz shows a pill that disagrees with its
    // own (not-yet-migrated) result prose (4-peer QC 2026-06-16: an on-page pill↔prose mismatch
    // is worse than the original pill↔product mismatch). presentedDriver is route-aware and
    // product-consistent (proven exact by check_quiz.py consistency.driver_matches_outcome).
    var pillCluster = (state.config && state.config.driver_pill_outcome_aligned)
      ? state.presentedDriver
      : state.dominantCluster;
    return CLUSTERS.map(function(c) {
      const pct = Math.min(100, Math.round(scores[c] / total * 100));
      const isDriver = c === pillCluster;
      const label = getClusterLabel(state, c);
      return [
        '<div class="bz-quiz__cluster-row' + (isDriver ? ' bz-quiz__cluster-row--dominant' : '') + '">',
        '  <div class="bz-quiz__cluster-label">',
        '    ' + escHtml(label),
        isDriver ? '    <span class="bz-quiz__cluster-tag">' + escHtml(primaryDriverLabel) + '</span>' : '',
        '  </div>',
        '  <div class="bz-quiz__cluster-bar" role="meter"',
        '    aria-valuenow="' + pct + '" aria-valuemin="0" aria-valuemax="100"',
        '    aria-label="' + escHtml(label) + ': ' + pct + '%">',
        '    <div class="bz-quiz__cluster-fill' + (isDriver ? ' bz-quiz__cluster-fill--dominant' : '') + '"',
        '      style="width:0%" data-target-width="' + pct + '%"></div>',
        '  </div>',
        '  <span class="bz-quiz__cluster-pct">' + pct + '%</span>',
        '</div>',
      ].join('');
    }).join('');
  }

  function renderStars(rating) {
    var stars = [];
    for (var i = 1; i <= 5; i++) {
      var filled = rating >= i - 0.25;
      var half   = !filled && rating >= i - 0.75;
      stars.push([
        '<g transform="translate(' + ((i - 1) * 16) + ', 0)">',
        '  <path d="M8 1l1.8 4.9H15l-4.2 3 1.6 5L8 11.3 3.6 13.9l1.6-5L1 5.9h5.2z"',
        '    fill="' + (filled ? '#02B780' : half ? 'url(#bz-half)' : '#E5E7EB') + '"/>',
        '</g>',
      ].join(''));
    }
    return '<defs><linearGradient id="bz-half" x1="0" x2="1" y1="0" y2="0"><stop offset="50%" stop-color="#02B780"/><stop offset="50%" stop-color="#E5E7EB"/></linearGradient></defs>' + stars.join('');
  }

  function paragraphsFromText(text) {
    if (!text) return '';
    return text.split('\n').filter(Boolean).map(function(p) { return '<p>' + escHtml(p) + '</p>'; }).join('');
  }

  /* ─────────────────────────────────────────────
     RENDER — WELCOME SCREEN
  ───────────────────────────────────────────── */

  function renderWelcomeScreen(state, container, onStart) {
    // Option A "Split" unified welcome (canonical 2026-06-09, OPTION-A-ROLLOUT-PLAN.md).
    // One unit = media column (hero) + copy column (heading/subhead/micro-meta/CTA/trust).
    // Hero URL is Liquid-assigned per market/variant via data-welcome-hero on the mount —
    // single source, also drives the <link rel=preload> in bz-quiz.liquid (QC #3).
    // No English engine defaults for localized copy (QC #1) → DE/FR never leak English.
    const cfg = state.config;
    const qTotal = cfg.phases ? cfg.phases.reduce(function(n, p) { return n + p.question_ids.length; }, 0) : 0;

    const heroUrl       = (container.getAttribute('data-welcome-hero') || '').trim();
    const hasHero       = heroUrl.length > 0;
    const heroAlt       = cfg.welcome_hero_alt || '';
    const heading       = cfg.welcome_heading || '';
    const headingAccent = cfg.welcome_heading_accent || '';
    const subhead       = cfg.welcome_subhead || '';
    const trustLine     = cfg.welcome_trust_line || '';
    const startLabel    = cfg.start_button_label || cfg.welcome_cta || 'Start the quiz';
    const complianceText = cfg.welcome_compliance_text || '';

    // Micro-meta — locale-safe: render a number with its label only when present.
    // No phases (kills the "1 phases" singular bug); free label optional.
    const questionsLabel = getCfg(state, 'welcome_meta_questions_label', '');
    const secondsLabel   = getCfg(state, 'welcome_meta_seconds_label', '');
    const freeLabel      = getCfg(state, 'welcome_meta_free_label', '');
    const metaItems = [];
    if (qTotal)        { metaItems.push(qTotal + (questionsLabel ? ' ' + questionsLabel : '')); }
    if (secondsLabel)  { metaItems.push(secondsLabel); }
    if (freeLabel)     { metaItems.push(freeLabel); }
    const metaHtml = metaItems.map(function(item, i) {
      return (i ? '<span class="bz-quiz__welcome-dot" aria-hidden="true"></span>' : '') +
             '<span>' + escHtml(item) + '</span>';
    }).join('');

    const headingHtml = heading
      ? '<h1 class="bz-quiz__welcome-heading">' + escHtml(heading) +
          (headingAccent ? '<br><span class="bz-quiz__welcome-accent">' + escHtml(headingAccent) + '</span>' : '') +
        '</h1>'
      : '';

    const copyHtml = [
      '<div class="bz-quiz__welcome-copy">',
      headingHtml,
      subhead    ? '<p class="bz-quiz__welcome-subhead">' + escHtml(subhead) + '</p>' : '',
      metaHtml   ? '<div class="bz-quiz__welcome-meta">' + metaHtml + '</div>' : '',
      '  <button class="bz-quiz__welcome-cta" type="button" id="bz-start-quiz">',
      '    <span>' + escHtml(startLabel) + '</span>',
      '    <span class="bz-quiz__welcome-cta-arr" aria-hidden="true">&rarr;</span>',
      '  </button>',
      trustLine  ? '<div class="bz-quiz__welcome-trust">' + escHtml(trustLine).replace(/^([★☆✰-✴\s]+)/, '<span class="bz-quiz__welcome-stars">$1</span>') + '</div>' : '',
      complianceText ? '<p class="bz-quiz__welcome-compliance">' + escHtml(complianceText) + '</p>' : '',
      '</div>',
    ].join('');

    const mediaHtml = hasHero
      ? '<div class="bz-quiz__welcome-media"><img src="' + escHtml(heroUrl) + '" alt="' + escHtml(heroAlt) + '" loading="eager" fetchpriority="high"></div>'
      : '';

    container.innerHTML = [
      '<div class="bz-quiz__welcome ' + (hasHero ? 'bz-quiz__welcome--split' : 'bz-quiz__welcome--copy-only') + '" role="main">',
      copyHtml,
      mediaHtml,
      '</div>',
    ].join('');

    container.setAttribute('data-screen', 'welcome');
    const startBtn = container.querySelector('#bz-start-quiz');
if (startBtn) { startBtn.addEventListener('click', onStart); }  }

  /* ─────────────────────────────────────────────
     UTILITIES
  ───────────────────────────────────────────── */

  function escHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /* ─────────────────────────────────────────────
     RELAY PAYLOAD
  ───────────────────────────────────────────── */

  function buildRelayPayload(state, email, firstName) {
    const cfg = state.config;
    const payload = {
      email:            email,
      first_name:       firstName,
      recommended_sku:  state.outcome,
      variant:          cfg.variant,
      market:           cfg.market,
      severity_score:   state.totalScore,
      dominant_cluster: state.dominantCluster,
      secondary_cluster: state.secondaryCluster,
      cause_flags:      buildCauseFlags(state.scores, state.totalScore),
      // T-fb0cd0: the answers themselves were never persisted (only 7 of 259 real submissions had
      // them), so no post-hoc analysis of WHY someone was routed anywhere was possible. The worker
      // already stores `responses` and metafields it; outcome_route and pole_scores make the v2
      // recommendation reconstructable from the record alone.
      responses:        Object.assign({}, state.answers),
      outcome_route:    state.outcomeRoute || null,
      pole_scores:      state.poles ? { pro: state.poles.pro, elite: state.poles.elite } : null,
      payload_schema:   2,
      // T-3bbb8c: which product the primary buy CTA pointed at, and whether the session arrived
      // paid-tagged. Flat bz_* keys are folded into the stored `utm` object by the Worker's
      // normalizeUtm (same path as bz_product / bz_test), so the readout can split the bundle
      // outcome by paid vs organic and see which CTA was actually shown.
      bz_cta:           resolveCTA(state).sku.toLowerCase(),
      bz_paid:          isPaidSession(state.utmParams) ? '1' : '0',
      plan_pdf_url:     null,
      bz_test:          cfg.utm_test_param,
      bz_product:       (state.outcome || '').toLowerCase(),
      submitted_at:     new Date().toISOString(),
    };
    Object.assign(payload, state.utmParams);
    return payload;
  }

  function buildCauseFlags(scores, total) {
    if (total === 0) return {};
    var flags = {};
    CLUSTERS.forEach(function(c) {
      flags[c] = Math.round(scores[c] / total * 100);
    });
    return flags;
  }

  /* ─────────────────────────────────────────────
     MAIN QUIZ CLASS
     KEY SHOPIFY CHANGE: init() reads config from a
     Liquid-injected <script type="application/json">
     blob instead of fetching an external JSON file.
  ───────────────────────────────────────────── */

  class BrainzymeQuiz {
    /**
     * @param {object} options
     * @param {string}   options.configScriptId — id of the <script type="application/json"> element
     *                                            containing the quiz config (Liquid-injected)
     * @param {string}   options.mountId        — id of the container element
     * @param {object}  [options.relay]         — relay-client instance (window.BrainzymeQuizRelay)
     */
    constructor({ configScriptId, mountId, relay }) {
      this.configScriptId = configScriptId || 'bz-quiz-config';
      this.mountId        = mountId;
      this.relay          = relay || null;
      this.container      = null;
      this.state          = null;
    }

    init() {
      this.container = document.getElementById(this.mountId);
      if (!this.container) {
        console.error('[BrainzymeQuiz] Mount element not found:', this.mountId);
        return;
      }

      this._renderSkeleton();

      // Read config from Liquid-injected JSON blob — no fetch needed
      let config;
      try {
        const configEl = document.getElementById(this.configScriptId);
        if (!configEl) throw new Error('Config script element not found: ' + this.configScriptId);
        config = JSON.parse(configEl.textContent);
      } catch (err) {
        console.error('[BrainzymeQuiz] Failed to parse config:', err);
        this._renderError();
        return;
      }

      this.state = freshState(config);
      this.state.utmParams = captureUTMParams();

      try {
        sessionStorage.setItem('bz_utm_params', JSON.stringify(this.state.utmParams));
        sessionStorage.setItem('bz_utm_test', config.utm_test_param);
      } catch (_) {}

      stampVariantAssignment(config.variant, config.market);

      // Funnel event (Step 8 rev — 3-reviewer QC pivot 2026-05-25):
      // emit bz_quiz_paused on EVERY visibility/unload signal (multi-fire
      // OK — each is valid data). Worker server-infers "abandoned" from
      // session_state with no terminal event after 30 min. visibilitychange
      // matters most for mobile Safari (70-80% of DTC traffic) which
      // suspends JS when page hidden — a setTimeout-based hybrid would
      // never fire on iOS, so we emit the data point IMMEDIATELY and
      // let the server-side aggregator decide what counts as abandoned.
      const self = this;
      const emitPaused = function(reason) {
        return function() {
          const s = self.state;
          if (!s) return;
          if (s.bzCompletedFired) return;
          if (s.emailSubmitted) return;
          if (!s.bzStartedFired) return;
          // visibilitychange fires on BOTH hidden+visible — only care about hidden
          if (reason === 'visibility_hidden' && typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
          publishFunnelEvent(s, 'bz_quiz_paused', {
            question_index:     s.globalQuestionIndex,
            total_questions:    s.totalQuestions,
            reached_email:      !!s.outcome,
            reached_processing: !!s.processingDone,
            pause_reason:       reason,
          });
        };
      };
      if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('beforeunload', emitPaused('beforeunload'));
        window.addEventListener('pagehide', emitPaused('pagehide'));
      }
      if (typeof document !== 'undefined' && document.addEventListener) {
        document.addEventListener('visibilitychange', emitPaused('visibility_hidden'));
      }

      const saved = loadProgress(config.variant, config.market);
      if (saved && Object.keys(saved.answers || {}).length > 0) {
        this._restoreProgress(saved);
        return;
      }

      this._renderWelcome();
    }

    _restoreProgress(saved) {
      const state = this.state;
      const savedAnswers = saved.answers || {};

      // T-3bbb8c (2026-09-08): restore attribution with fresh-wins precedence. init() captured the
      // CURRENT URL's tags; a paid arrival resumed from an untagged URL must still read paid (the
      // arrival WAS paid), and an organic-saved session resumed via a paid URL is now on an ad
      // destination. Before this, saved utmParams were written but never read back.
      state.utmParams = Object.assign({}, saved.utmParams || {}, captureUTMParams());

      // REPLAY rather than trust. Cached scores/poles were computed against whatever config was
      // live when they were saved; replaying the stored answer ids through applyAnswer rebuilds
      // both from the config that is live NOW.
      state.answers = {};
      state.scores = { sleep: 0, stress: 0, nutrition: 0, hormones: 0, overload: 0 };
      state.totalScore = 0;
      state.poles = { pro: 0, elite: 0 };
      Object.keys(savedAnswers).forEach(function (qId) {
        applyAnswer(state, qId, savedAnswers[qId]);
      });

      state.currentPhaseIndex = saved.currentPhaseIndex || 0;
      state.currentQuestionIndexInPhase = saved.currentQuestionIndexInPhase || 0;
      state.globalQuestionIndex = saved.globalQuestionIndex || 0;
      state.emailSubmitted = saved.emailSubmitted || false;

      // Only a session that had ALREADY reached a result gets one recomputed. A mid-quiz restore
      // returns to the question it left off at — otherwise a partially-answered session would jump
      // to results and skip the questions carrying the age / sensitivity gate.
      if (saved.outcome) {
        computeOutcome(state);
        this._renderResults();
      } else {
        state.outcome = null;
        state.outcomeRoute = null;
        state.presentedDriver = null;
        state.presentedDriverMode = null;
        this._renderQuestion();
      }
    }

    _renderSkeleton() {
      this.container.innerHTML = [
        '<div class="bz-quiz__skeleton">',
        '  <div class="bz-quiz__skeleton-bar" style="width:60%;height:10px;margin-bottom:12px;"></div>',
        '  <div class="bz-quiz__skeleton-bar" style="width:90%;height:24px;margin-bottom:20px;"></div>',
        '  <div class="bz-quiz__skeleton-bar" style="width:100%;height:56px;margin-bottom:12px;"></div>',
        '  <div class="bz-quiz__skeleton-bar" style="width:100%;height:56px;margin-bottom:12px;"></div>',
        '  <div class="bz-quiz__skeleton-bar" style="width:100%;height:56px;"></div>',
        '</div>',
      ].join('');
    }

    _renderError() {
      this.container.innerHTML = '<div class="bz-quiz__error" role="alert"><p>We couldn\'t load the quiz right now. Please refresh the page.</p></div>';
    }

    _renderWelcome() {
      renderWelcomeScreen(this.state, this.container, () => this._startQuiz());
    }

    _startQuiz() {
      // Retake fix (Umar feedback #7, 2026-05-19): clicking "Start the quiz"
      // wipes any prior saved progress + resets in-memory state so a user can
      // retake the quiz cleanly without being bounced straight to a stale
      // result page.
      clearProgress(this.state.config.variant, this.state.config.market);
      this.state = freshState(this.state.config);
      this.state.utmParams = captureUTMParams();
      saveProgress(this.state);
      // Funnel event (Step 8, 2026-05-25): user has begun the quiz session
      publishFunnelEvent(this.state, 'bz_quiz_started', {
        total_questions: this.state.totalQuestions,
      });
      this.state.bzStartedFired = true;
      this._renderQuestion({ focus: true });
    }

    _renderQuestion(options) {
      renderQuestionScreen(this.state, this.container, (qId, answerId) => this._handleAnswer(qId, answerId), options);
    }

    _handleAnswer(qId, answerId) {
      applyAnswer(this.state, qId, answerId);
      announceToSR('Answer recorded', this.container);
      // Funnel event (Step 8, 2026-05-25): user answered a question.
      // globalQuestionIndex is 0-based BEFORE advance; the event payload
      // reflects which question was just answered, not the next one.
      publishFunnelEvent(this.state, 'bz_quiz_question_advanced', {
        question_index:  this.state.globalQuestionIndex,
        total_questions: this.state.totalQuestions,
        qId:             qId,
        answerId:        answerId,
      });

      const phase = this.state.config.phases[this.state.currentPhaseIndex];
      const isLastQInPhase = this.state.currentQuestionIndexInPhase >= phase.question_ids.length - 1;
      const isLastPhase = this.state.currentPhaseIndex >= this.state.config.phases.length - 1;

      if (isLastQInPhase) {
        saveProgress(this.state);
        if (isLastPhase) {
          this._renderProcessing();
        } else {
          // Interstitial removed 2026-05-19 (Umar feedback #4). Phase advances
          // directly; the inline .bz-quiz__phase-header on the next question
          // (rendered when currentQuestionIndexInPhase === 0) provides the
          // section cue without a click-friction screen.
          this._advancePhase();
        }
      } else {
        this.state.currentQuestionIndexInPhase++;
        this.state.globalQuestionIndex++;
        saveProgress(this.state);
        this._animateQuestionTransition(() => this._renderQuestion({ focus: true }));
      }
    }

    _advancePhase() {
      this.state.currentPhaseIndex++;
      this.state.currentQuestionIndexInPhase = 0;
      this.state.globalQuestionIndex++;
      saveProgress(this.state);
      this._animatePhaseTransition(() => this._renderQuestion({ focus: true }));
    }

    _renderProcessing() {
      renderProcessingScreen(this.state, this.container, () => {
        computeOutcome(this.state);
        saveProgress(this.state);
        this._renderEmailGate();
      });
    }

    _renderEmailGate() {
      // Funnel event (Step 8, 2026-05-25): email gate has rendered. Computed
      // outcome already exists on state at this point (set in _renderProcessing).
      publishFunnelEvent(this.state, 'bz_quiz_email_shown', {
        outcome:     this.state.outcome,
        total_score: this.state.totalScore,
      });
      renderEmailGate(
        this.state, this.container,
        (email, firstName) => this._handleEmailSubmit(email, firstName),
        () => this._handleEmailSkip()
      );
    }

    _handleEmailSkip() {
      // Cycle-0 skip event (Calum GO 2026-07-17, session 32 / ClickUp 869dky980).
      // Fired BEFORE the results render (fire-then-render) so the event cannot be
      // lost to the transition. Splits skippers (saw results, no lead) from true
      // gate abandoners — previously both were invisible to the funnel.
      publishFunnelEvent(this.state, 'bz_quiz_email_skipped', {
        outcome:     this.state.outcome,
        total_score: this.state.totalScore,
      });
      // Best-effort GA4 mirror (Calum 2026-07-17: "track it with GA4 utm or other
      // methods"). Defensive: theme.liquid defines the gtag shim on ALL four stores
      // (verified 2026-09-15, T-fe3231) — the CF worker event above stays authoritative.
      try {
        if (typeof gtag === 'function') {
          gtag('event', 'bz_quiz_email_skipped', {
            quiz_market:  (this.state.config && this.state.config.market) || '',
            quiz_variant: (this.state.config && this.state.config.variant) || '',
            quiz_outcome: this.state.outcome || '',
          });
        }
      } catch (e) { /* analytics must never break the quiz */ }
      this._renderResults({ focus: true });
    }

    _handleEmailSubmit(email, firstName) {
      this.state.email = email;
      this.state.firstName = firstName;
      this.state.emailSubmitted = true;
      // Funnel event (Step 8, 2026-05-25): user clicked Submit on the email
      // gate. Fires BEFORE the Worker fetch so even network failures count
      // as "attempted" in funnel reporting.
      publishFunnelEvent(this.state, 'bz_quiz_submit_attempted', {
        has_email:   !!email,
        has_name:    !!firstName,
        outcome:     this.state.outcome,
      });

      const payload = buildRelayPayload(this.state, email, firstName);
      console.log('[BrainzymeQuiz] Lead payload:', JSON.stringify(payload, null, 2));

      // T-38bf38: bind the callbacks to THIS attempt. reset() (retake) replaces this.state, so a
      // relay that resolves after a retake must still report the submitted attempt, not the new one.
      const attempt = this.state;
      if (this.relay && typeof this.relay.submitToRelay === 'function') {
        this.relay.submitToRelay(payload, {
          source: attempt.config.variant + '-' + attempt.config.market,
        }).then(function(ok) {
          if (ok) {
            // Funnel event: Worker accepted the submission (forwarded or queued for retry)
            publishFunnelEvent(attempt, 'bz_quiz_completed', {
              outcome:         attempt.outcome,
              recommended_sku: attempt.outcome, // alias for downstream that uses recommended_sku schema (Codex + Gemini QC 2026-05-25)
              total_score:     attempt.totalScore,
              cta_sku:         resolveCTA(attempt).sku, // T-3bbb8c: what the buy button pointed at
            });
            // T-fe3231 (2026-09-15): GA4 never received bz_quiz_completed — only the skip handler
            // mirrored to gtag. Same guarded shape as bz_quiz_email_skipped; the native Google app
            // pixel only emits standard commerce events, so this cannot double-count.
            try {
              if (typeof gtag === 'function') {
                gtag('event', 'bz_quiz_completed', {
                  quiz_market:  (attempt.config && attempt.config.market) || '',
                  quiz_variant: (attempt.config && attempt.config.variant) || '',
                  quiz_outcome: attempt.outcome || '',
                });
              }
            } catch (e) { /* analytics must never break the quiz */ }
            attempt.bzCompletedFired = true;
          } else {
            // Relay returned falsy = queued locally; submit_failed but not catastrophic
            publishFunnelEvent(attempt, 'bz_quiz_submit_failed', {
              reason:  'queued_to_localstorage',
              outcome: attempt.outcome,
            });
            console.warn('[BrainzymeQuiz] Relay: queued to localStorage');
          }
        }).catch(function(err) {
          publishFunnelEvent(attempt, 'bz_quiz_submit_failed', {
            reason:  'relay_exception',
            outcome: attempt.outcome,
            error:   String((err && err.message) || err).slice(0, 200),
          });
        });
      }

      saveProgress(this.state);
      this._renderResults({ focus: true });
    }

    _renderResults(options) {
      renderResultsPage(this.state, this.container, (type, sku, url) => this._handleCTAClick(type, sku, url), options);
    }

    _handleCTAClick(type, sku, url) {
      if (type === 'primary') {
        // T-40a3cd: fire-and-forget; the <a> keeps its default navigation.
        postQuizCartAttributes(this.state, resolveCTA(this.state));
      } else if (type === 'secondary' && !this.state.emailSubmitted) {
        this._renderEmailGate();
      } else if (type === 'retake') {
        this.reset();
      }
    }

    _animateQuestionTransition(callback) {
      if (prefersReducedMotion()) { callback(); return; }
      this.container.classList.add('bz-quiz--slide-out');
      const timeout = 180;
      setTimeout(() => {
        this.container.classList.remove('bz-quiz--slide-out');
        callback();
        this.container.classList.add('bz-quiz--slide-in');
        setTimeout(() => { this.container.classList.remove('bz-quiz--slide-in'); }, timeout);
      }, timeout);
    }

    _animatePhaseTransition(callback) {
      if (prefersReducedMotion()) { callback(); return; }
      this.container.classList.add('bz-quiz--fade-out');
      setTimeout(() => {
        this.container.classList.remove('bz-quiz--fade-out');
        callback();
        this.container.classList.add('bz-quiz--fade-in');
        setTimeout(() => { this.container.classList.remove('bz-quiz--fade-in'); }, 300);
      }, 250);
    }

    reset() {
      clearProgress(this.state.config.variant, this.state.config.market);
      this.state = freshState(this.state.config);
      this.state.utmParams = captureUTMParams();
      this._renderWelcome();
      // Retake viewport fix (Elham via Calum, 2026-08-19): the results screen is
      // much taller than the welcome screen. Resetting collapses the quiz ABOVE
      // the viewport, leaving the user staring at whatever section sits below it
      // (on the homepage: the video / influencer carousel) — which reads as
      // "retry sent me somewhere else" even though no navigation happened.
      // Reproduced by click-through: scrollY stayed at 1348px while the quiz top
      // sat at -1233px. So: bring the quiz back into view. Never let this throw —
      // a failed scroll must not break the reset itself.
      try {
        // QC refinements (6-peer panel 2026-08-19):
        // - scroll-margin keeps the welcome heading clear of sticky headers
        //   (announcement bar / nav) instead of tucking it underneath y=0.
        // - focus moves to the quiz container for keyboard / screen-reader
        //   users, whose focus otherwise dies with the removed retake button.
        //   preventScroll so the explicit scrollIntoView below owns movement.
        this.container.style.scrollMarginTop = '72px';
        if (!this.container.hasAttribute('tabindex')) {
          this.container.setAttribute('tabindex', '-1');
        }
        try { this.container.focus({ preventScroll: true }); } catch (e2) { /* non-fatal */ }
        this.container.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });
      } catch (e) { /* non-fatal */ }
    }

    /* Expose internals for unit tests */
    static _computeOutcome(mockState) {
      computeOutcome(mockState);
      return {
        outcome:         mockState.outcome,
        outcomeRoute:    mockState.outcomeRoute,
        dominantCluster: mockState.dominantCluster,
        secondaryCluster: mockState.secondaryCluster,
        presentedDriver: mockState.presentedDriver,
        presentedDriverMode: mockState.presentedDriverMode,
        totalScore:      mockState.totalScore,
        scores:          Object.assign({}, mockState.scores),
        poles:           Object.assign({ pro: 0, elite: 0 }, mockState.poles || {}),
      };
    }

    static _applyAnswer(state, qId, answerId) { return applyAnswer(state, qId, answerId); }
    static _freshState(config) { return freshState(config); }
    // T-3bbb8c fixture hooks (tools/_quiz_cta_fixture.js): the buy-block resolver and its inputs.
    static _resolveCTA(state) { return resolveCTA(state); }
    static _isPaidSession(utm) { return isPaidSession(utm); }
    static _buildCheckoutURL(state, skuOverride) { return buildCheckoutURL(state, skuOverride); }
    // T-40a3cd / T-44e3b0 fixture hooks (ws1 test): cart attribution + the QA marker.
    static _postQuizCartAttributes(state, cta) { return postQuizCartAttributes(state, cta); }
    static _readQaFlag() { return readQaFlag(); }
    static _sameStoreHost(a, b) { return sameStoreHost(a, b); }
  }

  global.BrainzymeQuiz = BrainzymeQuiz;

}(window));
